import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { sanitizeHtml } from "@/components/RichHtmlEditorDialog";
import { ArrowLeft, PencilSimple, Books } from "@phosphor-icons/react";

const AUDIENCE_LABEL = { sales: "Sales", support: "Support" };

export default function StaffKnowledgeBaseView() {
  const { kbId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/staff-knowledge-bases/${kbId}`);
        if (!cancelled) setDoc(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
        if (!cancelled) navigate("/staff-knowledge-bases", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kbId, navigate]);

  if (loading) {
    return <p className="text-sm text-muted-foreground p-2">Loading article…</p>;
  }
  if (!doc) return null;

  return (
    <div className="max-w-3xl mx-auto" data-testid="skb-read-page">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Button
          variant="outline"
          size="sm"
          className="rounded-lg"
          onClick={() => navigate("/staff-knowledge-bases")}
          data-testid="skb-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to list
        </Button>
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-lg"
            asChild
          >
            <Link to={`/staff-knowledge-bases?edit=${doc.id}`}>
              <PencilSimple className="w-4 h-4 mr-1.5" /> Edit
            </Link>
          </Button>
        )}
      </div>

      <article className="rounded-2xl border border-border bg-card overflow-hidden shadow-sm">
        <header className="relative px-6 sm:px-10 pt-8 pb-6 border-b border-border bg-gradient-to-br from-teal-50 via-card to-sky-50 dark:from-teal-950/40 dark:via-card dark:to-sky-950/30">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-teal-600 text-white grid place-items-center shrink-0">
              <Books className="w-5 h-5" weight="duotone" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="inline-flex items-center rounded-full border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-950/50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-teal-800 dark:text-teal-200">
                  {AUDIENCE_LABEL[doc.audience] || doc.audience}
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight leading-tight" data-testid="skb-read-title">
                {doc.name}
              </h1>
            </div>
          </div>
        </header>

        <div
          className="skb-prose px-6 sm:px-10 py-8"
          data-testid="skb-read-content"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(doc.content || "") }}
        />
      </article>

      <style>{`
        .skb-prose {
          color: hsl(var(--foreground));
          font-size: 15px;
          line-height: 1.7;
        }
        .skb-prose h2 {
          font-size: 1.35rem;
          font-weight: 650;
          margin: 2rem 0 0.75rem;
          letter-spacing: -0.02em;
          padding-bottom: 0.4rem;
          border-bottom: 1px solid hsl(var(--border));
        }
        .skb-prose h2:first-child { margin-top: 0; }
        .skb-prose h3 {
          font-size: 1.05rem;
          font-weight: 600;
          margin: 1.5rem 0 0.5rem;
        }
        .skb-prose p { margin: 0.65rem 0; color: hsl(var(--muted-foreground)); }
        .skb-prose p b, .skb-prose p strong, .skb-prose li b, .skb-prose li strong {
          color: hsl(var(--foreground));
          font-weight: 600;
        }
        .skb-prose ul, .skb-prose ol { margin: 0.75rem 0 0.75rem 1.25rem; }
        .skb-prose li { margin: 0.35rem 0; color: hsl(var(--muted-foreground)); }
        .skb-prose blockquote {
          margin: 1rem 0;
          padding: 0.85rem 1rem;
          border-left: 3px solid #0d9488;
          background: hsl(var(--muted) / 0.45);
          border-radius: 0 0.75rem 0.75rem 0;
          color: hsl(var(--foreground));
        }
        .skb-prose pre {
          margin: 1rem 0;
          padding: 1rem 1.1rem;
          border-radius: 0.75rem;
          background: #0f172a;
          color: #e2e8f0;
          overflow-x: auto;
          font-size: 12.5px;
          line-height: 1.55;
        }
        .skb-prose code {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 0.86em;
        }
        .skb-prose :not(pre) > code {
          background: hsl(var(--muted));
          padding: 0.1rem 0.35rem;
          border-radius: 0.35rem;
          color: hsl(var(--foreground));
        }
        .skb-prose table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0 1.25rem;
          font-size: 13.5px;
          overflow: hidden;
          border-radius: 0.75rem;
          border: 1px solid hsl(var(--border));
        }
        .skb-prose thead th {
          background: hsl(var(--muted) / 0.7);
          text-align: left;
          font-weight: 600;
          color: hsl(var(--foreground));
          padding: 0.65rem 0.85rem;
          border-bottom: 1px solid hsl(var(--border));
        }
        .skb-prose td {
          padding: 0.6rem 0.85rem;
          border-top: 1px solid hsl(var(--border));
          vertical-align: top;
          color: hsl(var(--muted-foreground));
        }
        .skb-prose tr:nth-child(even) td { background: hsl(var(--muted) / 0.25); }
        .skb-prose img, .skb-prose figure {
          display: block;
          width: 100%;
          max-width: 100%;
          margin: 1.25rem 0;
          border-radius: 0.85rem;
        }
        .skb-prose figcaption {
          margin-top: 0.45rem;
          font-size: 12px;
          text-align: center;
          color: hsl(var(--muted-foreground));
        }
        .skb-prose hr {
          border: none;
          border-top: 1px solid hsl(var(--border));
          margin: 1.75rem 0;
        }
      `}</style>
    </div>
  );
}
